import React, { useState, useEffect, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import {
  X,
  Settings,
  Globe,
  Image,
  FileText,
  Upload,
  Link as LinkIcon,
  Search,
  Share2,
  Loader2,
  BarChart3,
  Eye,
  EyeOff,
  MousePointer,
  Activity,
  Zap,
  ExternalLink,
  Copy,
  Check,
  Target,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../../services/api';

interface LinkedPixel {
  platform: string;
  pixel_id: string;
  pixel_name: string;
  events?: string[];
  conversions_api_token?: string;
  test_event_code?: string;
  conversion_label?: string;
  google_tag_id?: string;
  container_id?: string;
  account_id?: string;
  account_name?: string;
}

interface PageSettings {
  name: string;
  slug: string;
  meta_title: string;
  meta_description: string;
  meta_keywords: string;
  og_image: string;
  favicon: string;
  flux_enabled?: boolean;
  flux_api_key?: string;
  flux_app_id?: string;
  linked_pixels?: LinkedPixel[];
}

interface PageSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  settings: PageSettings;
  onSave: (settings: PageSettings) => void;
  /** Estado de publicação da página — usado para avisar que os pixels salvos ainda não estão no ar. */
  publishInfo?: {
    status?: string;
    updatedAt?: string | null;
    lastDeployedAt?: string | null;
  };
}

// Publicar grava `last_deployed_at` junto com o `updated_at`, então uma folga de
// alguns segundos evita alarme falso logo depois de publicar.
const PUBLISH_TOLERANCE_MS = 2 * 60 * 1000;

const PageSettingsModal: React.FC<PageSettingsModalProps> = ({
  isOpen,
  onClose,
  settings,
  onSave,
  publishInfo
}) => {
  const { t } = useTranslation();
  const [localSettings, setLocalSettings] = useState<PageSettings>(settings);
  const [activeTab, setActiveTab] = useState<'general' | 'seo' | 'social' | 'analytics' | 'pixels'>('general');
  const [isUploadingFavicon, setIsUploadingFavicon] = useState(false);
  const [isUploadingOgImage, setIsUploadingOgImage] = useState(false);
  const [copiedScript, setCopiedScript] = useState(false);
  const faviconInputRef = useRef<HTMLInputElement>(null);
  const ogImageInputRef = useRef<HTMLInputElement>(null);

  // Pixel state
  const [platformPixels, setPlatformPixels] = useState<{ meta: any[]; google: any[]; linkedin: any[] }>({ meta: [], google: [], linkedin: [] });
  const [isLoadingPixels, setIsLoadingPixels] = useState(false);

  // Pixel só entra no HTML no momento de publicar. Salvar e não publicar era a
  // armadilha silenciosa: a tela dizia "vinculado" e o Tag Assistant não achava nada.
  const pixelsAguardandoPublicacao = React.useMemo(() => {
    if (!publishInfo) return false;
    if (!publishInfo.lastDeployedAt) return true;
    if (!publishInfo.updatedAt) return false;
    const alterado = new Date(publishInfo.updatedAt).getTime();
    const publicado = new Date(publishInfo.lastDeployedAt).getTime();
    if (Number.isNaN(alterado) || Number.isNaN(publicado)) return false;
    return alterado - publicado > PUBLISH_TOLERANCE_MS;
  }, [publishInfo?.updatedAt, publishInfo?.lastDeployedAt]);
  const [pixelsLoaded, setPixelsLoaded] = useState(false);
  const [showCapiToken, setShowCapiToken] = useState(false);
  const [gtmContainerId, setGtmContainerId] = useState('');

  const META_EVENTS = ['PageView', 'Lead', 'ViewContent', 'AddToCart', 'Purchase', 'CompleteRegistration', 'Subscribe', 'Contact', 'InitiateCheckout'];

  useEffect(() => {
    setLocalSettings(settings);
    // Initialize GTM container ID from existing linked_pixels
    const gtmPixel = (settings.linked_pixels || []).find(p => p.platform === 'gtm');
    setGtmContainerId(gtmPixel?.container_id || gtmPixel?.pixel_id || '');
  }, [settings]);

  // Load pixels when pixel tab is activated
  useEffect(() => {
    if (activeTab === 'pixels' && !pixelsLoaded) {
      loadPixels();
    }
  }, [activeTab]);

  const loadPixels = async () => {
    setIsLoadingPixels(true);
    try {
      const res = await api.get('/campaigns/pixels/available');
      if (res.data?.success && res.data?.data) {
        setPlatformPixels(res.data.data);
        setPixelsLoaded(true);
      }
    } catch (err) {
      console.error('Error loading pixels:', err);
      toast.error(t('pageBuilder.settings.pixels.loadError', 'Erro ao carregar pixels. Tente novamente.'));
      // Don't set pixelsLoaded so user can retry by switching tabs
    } finally {
      setIsLoadingPixels(false);
    }
  };

  const togglePixel = (platform: string, pixelId: string, pixelName: string, extra?: { conversion_label?: string; google_tag_id?: string; account_id?: string; account_name?: string }) => {
    setLocalSettings(prev => {
      const currentPixels = prev.linked_pixels || [];
      const exists = currentPixels.some(p => p.platform === platform && p.pixel_id === pixelId);
      let newPixels: LinkedPixel[];
      if (exists) {
        // Remove this specific pixel
        newPixels = currentPixels.filter(p => !(p.platform === platform && p.pixel_id === pixelId));
      } else {
        const newPixel: LinkedPixel = { platform, pixel_id: pixelId, pixel_name: pixelName };
        if (platform === 'google') {
          // Google: allow MULTIPLE conversion actions (toggle on/off individually)
          if (extra) {
            if (extra.conversion_label) newPixel.conversion_label = extra.conversion_label;
            if (extra.google_tag_id) newPixel.google_tag_id = extra.google_tag_id;
            // conta dona da conversão — necessária p/ o envio server-side ir na conta certa
            if (extra.account_id) newPixel.account_id = extra.account_id;
            if (extra.account_name) newPixel.account_name = extra.account_name;
          }
          newPixels = [...currentPixels, newPixel];
        } else {
          // Meta/LinkedIn: single pixel per platform (replace old one)
          const oldPixel = currentPixels.find(p => p.platform === platform);
          if (platform === 'meta') {
            newPixel.events = oldPixel?.events || ['PageView'];
            if (oldPixel?.conversions_api_token) newPixel.conversions_api_token = oldPixel.conversions_api_token;
            if (oldPixel?.test_event_code) newPixel.test_event_code = oldPixel.test_event_code;
          }
          newPixels = [...currentPixels.filter(p => p.platform !== platform), newPixel];
        }
      }
      return { ...prev, linked_pixels: newPixels };
    });
  };

  const isPixelLinked = (platform: string, pixelId: string) => {
    return (localSettings.linked_pixels || []).some(p => p.platform === platform && p.pixel_id === pixelId);
  };

  const getLinkedMetaPixel = (): LinkedPixel | undefined => {
    return (localSettings.linked_pixels || []).find(p => p.platform === 'meta');
  };

  const toggleMetaEvent = (eventName: string) => {
    setLocalSettings(prev => {
      const pixels = [...(prev.linked_pixels || [])];
      const metaIdx = pixels.findIndex(p => p.platform === 'meta');
      if (metaIdx === -1) return prev;
      const pixel = { ...pixels[metaIdx] };
      const events = [...(pixel.events || ['PageView'])];
      const idx = events.indexOf(eventName);
      if (idx >= 0) {
        // Don't allow removing PageView
        if (eventName === 'PageView') return prev;
        events.splice(idx, 1);
      } else {
        events.push(eventName);
      }
      pixel.events = events;
      pixels[metaIdx] = pixel;
      return { ...prev, linked_pixels: pixels };
    });
  };

  const updateMetaPixelField = (field: 'conversions_api_token' | 'test_event_code', value: string) => {
    setLocalSettings(prev => {
      const pixels = [...(prev.linked_pixels || [])];
      const metaIdx = pixels.findIndex(p => p.platform === 'meta');
      if (metaIdx === -1) return prev;
      const pixel = { ...pixels[metaIdx], [field]: value };
      pixels[metaIdx] = pixel;
      return { ...prev, linked_pixels: pixels };
    });
  };

  const updateGooglePixelLabel = (pixelId: string, label: string) => {
    setLocalSettings(prev => {
      const pixels = [...(prev.linked_pixels || [])];
      const idx = pixels.findIndex(p => p.platform === 'google' && p.pixel_id === pixelId);
      if (idx === -1) return prev;
      pixels[idx] = { ...pixels[idx], conversion_label: label.trim() };
      return { ...prev, linked_pixels: pixels };
    });
  };

  const saveGtmContainer = (containerId: string) => {
    const trimmed = containerId.trim().toUpperCase();
    setGtmContainerId(trimmed);
    setLocalSettings(prev => {
      const pixels = (prev.linked_pixels || []).filter(p => p.platform !== 'gtm');
      if (trimmed && /^GTM-[A-Z0-9]+$/.test(trimmed)) {
        pixels.push({ platform: 'gtm', pixel_id: trimmed, pixel_name: `GTM ${trimmed}`, container_id: trimmed });
      }
      return { ...prev, linked_pixels: pixels };
    });
  };

  const removeGtmContainer = () => {
    setGtmContainerId('');
    setLocalSettings(prev => ({
      ...prev,
      linked_pixels: (prev.linked_pixels || []).filter(p => p.platform !== 'gtm')
    }));
  };

  const handleChange = (field: keyof PageSettings, value: string | boolean) => {
    setLocalSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    onSave(localSettings);
    onClose();
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleNameChange = (name: string) => {
    setLocalSettings(prev => ({
      ...prev,
      name,
      slug: prev.slug || generateSlug(name),
      meta_title: prev.meta_title || name
    }));
  };

  const handleFileUpload = async (file: File, type: 'favicon' | 'og_image') => {
    const setUploading = type === 'favicon' ? setIsUploadingFavicon : setIsUploadingOgImage;
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/upload/image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data?.success && response.data?.url) {
        handleChange(type, response.data.url);
        toast.success(type === 'favicon' ? t('pageBuilder.settings.faviconUploaded', 'Favicon uploaded!') : t('pageBuilder.settings.imageUploaded', 'Image uploaded!'));
      } else {
        throw new Error('Upload failed');
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.message || t('pageBuilder.settings.uploadError', 'Error uploading'));
    } finally {
      setUploading(false);
    }
  };

  const tabs = [
    { id: 'general', label: t('pageBuilder.settings.tabs.general', 'Geral'), icon: Settings },
    { id: 'seo', label: t('pageBuilder.settings.tabs.seo', 'SEO'), icon: Search },
    { id: 'social', label: t('pageBuilder.settings.tabs.social', 'Social'), icon: Share2 },
    { id: 'pixels', label: 'Pixels', icon: Target },
    { id: 'analytics', label: t('pageBuilder.settings.tabs.analytics', 'Analytics'), icon: BarChart3 }
  ];

  const copyFluxScript = () => {
    const script = generateFluxScript();
    navigator.clipboard.writeText(script);
    setCopiedScript(true);
    toast.success(t('pageBuilder.settings.analytics.scriptCopied', 'Script copied!'));
    setTimeout(() => setCopiedScript(false), 2000);
  };

  const generateFluxScript = () => {
    const apiKey = localSettings.flux_api_key || '';
    const appId = localSettings.flux_app_id || '';
    return `<script>
  (function(w,d,c,a,b){
    if(/bot|crawler|spider|lighthouse|pagespeed|gtmetrix|headless/i.test(navigator.userAgent))return;
    w.DevSkin=w.DevSkin||function(){(w.DevSkin.q=w.DevSkin.q||[]).push(Array.prototype.slice.call(arguments))};
    w.DevSkin.l=Date.now();
    w.DevSkin('init',c);
    a=d.getElementsByTagName('head')[0];
    b=d.createElement('script');
    b.async=1;
    b.src='https://cdn.jsdelivr.net/npm/@devskin/browser-sdk@latest/dist/devskin.umd.min.js';
    a.appendChild(b);
  })(window,document,{
    apiKey: '${apiKey}',
    appId: '${appId}',
    apiUrl: 'https://api-monitoring.devskin.com',
    sessionRecording: { enabled: true, maskAllInputs: true, sampling: 1 },
    heatmapOptions: { enabled: true, trackClicks: true, trackScroll: true, trackMouseMovement: true, mouseMoveSampling: 0.1 },
    captureWebVitals: true,
    captureNetworkRequests: true,
    captureErrors: true,
    privacy: { respectDoNotTrack: true, cookieConsent: false },
    environment: 'production',
    debug: false
  });
</script>`;
  };

  return (
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        <Transition.Child
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-gray-900 border border-gray-700 shadow-2xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
                  <Dialog.Title className="text-xl font-semibold text-white flex items-center gap-2">
                    <Settings className="w-5 h-5 text-indigo-400" />
                    {t('pageBuilder.settings.title', 'Configuracoes da Pagina')}
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-400" />
                  </button>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-gray-700">
                  {tabs.map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id as any)}
                      className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors ${
                        activeTab === tab.id
                          ? 'text-indigo-400 border-b-2 border-indigo-400 bg-indigo-500/10'
                          : 'text-gray-400 hover:text-white hover:bg-gray-800'
                      }`}
                    >
                      <tab.icon className="w-4 h-4" />
                      {tab.label}
                    </button>
                  ))}
                </div>

                {/* Content */}
                <div className="p-6 space-y-6 max-h-[60vh] overflow-y-auto">
                  {/* General Tab */}
                  {activeTab === 'general' && (
                    <div className="space-y-5">
                      {/* Name */}
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          {t('pageBuilder.settings.general.name', 'Nome da Pagina')}
                        </label>
                        <input
                          type="text"
                          value={localSettings.name}
                          onChange={(e) => handleNameChange(e.target.value)}
                          placeholder={t('pageBuilder.settings.general.namePlaceholder', 'Minha Landing Page')}
                          className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-xl text-white placeholder-gray-500 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors"
                        />
                      </div>

                      {/* Slug */}
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          {t('pageBuilder.settings.general.slug', 'URL (Slug)')}
                        </label>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 flex items-center bg-gray-800 border border-gray-600 rounded-xl overflow-hidden">
                            <span className="px-3 py-3 bg-gray-700 text-gray-400 text-sm border-r border-gray-600">
                              seusite.vercel.app/
                            </span>
                            <input
                              type="text"
                              value={localSettings.slug}
                              onChange={(e) => handleChange('slug', generateSlug(e.target.value))}
                              placeholder="minha-pagina"
                              className="flex-1 px-3 py-3 bg-transparent text-white placeholder-gray-500 focus:outline-none"
                            />
                          </div>
                        </div>
                        <p className="mt-1 text-xs text-gray-500">
                          {t('pageBuilder.settings.general.slugHelp', 'Apenas letras minusculas, numeros e hifens')}
                        </p>
                      </div>

                      {/* Favicon */}
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          {t('pageBuilder.settings.general.favicon', 'Favicon')}
                        </label>
                        <div className="flex items-center gap-4">
                          {localSettings.favicon ? (
                            <div className="w-12 h-12 bg-gray-800 rounded-lg border border-gray-600 flex items-center justify-center overflow-hidden">
                              <img
                                src={localSettings.favicon}
                                alt="Favicon"
                                className="w-8 h-8 object-contain"
                              />
                            </div>
                          ) : (
                            <div className="w-12 h-12 bg-gray-800 rounded-lg border border-gray-600 border-dashed flex items-center justify-center">
                              <Globe className="w-5 h-5 text-gray-500" />
                            </div>
                          )}
                          <div className="flex-1">
                            <input
                              type="text"
                              value={localSettings.favicon}
                              onChange={(e) => handleChange('favicon', e.target.value)}
                              placeholder="https://exemplo.com/favicon.ico"
                              className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                            />
                          </div>
                          <input
                            ref={faviconInputRef}
                            type="file"
                            accept="image/*,.ico"
                            className="hidden"
                            onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0], 'favicon')}
                          />
                          <button
                            onClick={() => faviconInputRef.current?.click()}
                            disabled={isUploadingFavicon}
                            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm flex items-center gap-2 transition-colors disabled:opacity-50"
                          >
                            <Upload className="w-4 h-4" />
                            {isUploadingFavicon ? t('common.loading', 'Enviando...') : t('pageBuilder.settings.general.upload', 'Upload')}
                          </button>
                        </div>
                        <p className="mt-1 text-xs text-gray-500">
                          {t('pageBuilder.settings.general.faviconHelp', 'Recomendado: 32x32px ou 64x64px, formato .ico ou .png')}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* SEO Tab */}
                  {activeTab === 'seo' && (
                    <div className="space-y-5">
                      {/* Meta Title */}
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          {t('pageBuilder.settings.seo.metaTitle', 'Titulo SEO (Meta Title)')}
                        </label>
                        <input
                          type="text"
                          value={localSettings.meta_title}
                          onChange={(e) => handleChange('meta_title', e.target.value)}
                          placeholder={t('pageBuilder.settings.seo.metaTitlePlaceholder', 'Titulo que aparece no Google')}
                          maxLength={70}
                          className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-xl text-white placeholder-gray-500 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                        />
                        <div className="flex justify-between mt-1">
                          <p className="text-xs text-gray-500">{t('pageBuilder.settings.seo.metaTitleHelp', 'Aparece na aba do navegador e nos resultados do Google')}</p>
                          <span className={`text-xs ${localSettings.meta_title.length > 60 ? 'text-yellow-500' : 'text-gray-500'}`}>
                            {localSettings.meta_title.length}/70
                          </span>
                        </div>
                      </div>

                      {/* Meta Description */}
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          {t('pageBuilder.settings.seo.metaDescription', 'Descricao SEO (Meta Description)')}
                        </label>
                        <textarea
                          value={localSettings.meta_description}
                          onChange={(e) => handleChange('meta_description', e.target.value)}
                          placeholder={t('pageBuilder.settings.seo.metaDescriptionPlaceholder', 'Descricao que aparece nos resultados de busca do Google')}
                          maxLength={160}
                          rows={3}
                          className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-xl text-white placeholder-gray-500 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 resize-none"
                        />
                        <div className="flex justify-between mt-1">
                          <p className="text-xs text-gray-500">{t('pageBuilder.settings.seo.metaDescriptionHelp', 'Resumo que aparece abaixo do titulo no Google')}</p>
                          <span className={`text-xs ${localSettings.meta_description.length > 155 ? 'text-yellow-500' : 'text-gray-500'}`}>
                            {localSettings.meta_description.length}/160
                          </span>
                        </div>
                      </div>

                      {/* Keywords */}
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          {t('pageBuilder.settings.seo.keywords', 'Palavras-chave')}
                        </label>
                        <input
                          type="text"
                          value={localSettings.meta_keywords}
                          onChange={(e) => handleChange('meta_keywords', e.target.value)}
                          placeholder={t('pageBuilder.settings.seo.keywordsPlaceholder', 'landing page, conversao, marketing digital')}
                          className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-xl text-white placeholder-gray-500 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          {t('pageBuilder.settings.seo.keywordsHelp', 'Separe por virgulas. Usado para indexacao.')}
                        </p>
                      </div>

                      {/* Preview */}
                      <div className="p-4 bg-gray-800 rounded-xl border border-gray-700">
                        <p className="text-xs text-gray-500 mb-3">{t('pageBuilder.settings.seo.preview', 'Preview no Google:')}</p>
                        <div className="space-y-1">
                          <p className="text-blue-400 text-lg hover:underline cursor-pointer truncate">
                            {localSettings.meta_title || localSettings.name || t('pageBuilder.settings.seo.defaultTitle', 'Titulo da Pagina')}
                          </p>
                          <p className="text-green-400 text-sm truncate">
                            seusite.vercel.app/{localSettings.slug || 'pagina'}
                          </p>
                          <p className="text-gray-400 text-sm line-clamp-2">
                            {localSettings.meta_description || t('pageBuilder.settings.seo.defaultDescription', 'Adicione uma descricao para sua pagina aparecer melhor nos resultados de busca.')}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Social Tab */}
                  {activeTab === 'social' && (
                    <div className="space-y-5">
                      {/* OG Image */}
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          {t('pageBuilder.settings.social.ogImage', 'Imagem de Compartilhamento (OG Image)')}
                        </label>
                        <div className="space-y-3">
                          {localSettings.og_image ? (
                            <div className="relative aspect-video bg-gray-800 rounded-xl border border-gray-600 overflow-hidden">
                              <img
                                src={localSettings.og_image}
                                alt="OG Preview"
                                className="w-full h-full object-cover"
                              />
                              <button
                                onClick={() => handleChange('og_image', '')}
                                className="absolute top-2 right-2 p-1.5 bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
                              >
                                <X className="w-4 h-4 text-white" />
                              </button>
                            </div>
                          ) : (
                            <div
                              onClick={() => ogImageInputRef.current?.click()}
                              className="aspect-video bg-gray-800 rounded-xl border-2 border-gray-600 border-dashed flex flex-col items-center justify-center cursor-pointer hover:border-indigo-500 hover:bg-gray-800/50 transition-colors"
                            >
                              <Image className="w-10 h-10 text-gray-500 mb-2" />
                              <p className="text-gray-400 text-sm">{t('pageBuilder.settings.social.clickToUpload', 'Clique para fazer upload')}</p>
                              <p className="text-gray-500 text-xs mt-1">{t('pageBuilder.settings.social.recommended', '1200x630px recomendado')}</p>
                            </div>
                          )}
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={localSettings.og_image}
                              onChange={(e) => handleChange('og_image', e.target.value)}
                              placeholder="https://exemplo.com/imagem.jpg"
                              className="flex-1 px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                            />
                            <input
                              ref={ogImageInputRef}
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0], 'og_image')}
                            />
                            <button
                              onClick={() => ogImageInputRef.current?.click()}
                              disabled={isUploadingOgImage}
                              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm flex items-center gap-2 transition-colors disabled:opacity-50"
                            >
                              <Upload className="w-4 h-4" />
                              {isUploadingOgImage ? t('common.loading', 'Enviando...') : t('pageBuilder.settings.general.upload', 'Upload')}
                            </button>
                          </div>
                        </div>
                        <p className="mt-2 text-xs text-gray-500">
                          {t('pageBuilder.settings.social.ogImageHelp', 'Imagem que aparece ao compartilhar no Facebook, Twitter, LinkedIn, WhatsApp, etc.')}
                        </p>
                      </div>

                      {/* Social Preview */}
                      <div className="p-4 bg-gray-800 rounded-xl border border-gray-700">
                        <p className="text-xs text-gray-500 mb-3">{t('pageBuilder.settings.social.preview', 'Preview no Facebook/LinkedIn:')}</p>
                        <div className="bg-white rounded-lg overflow-hidden max-w-sm">
                          <div className="aspect-video bg-gray-200">
                            {localSettings.og_image ? (
                              <img
                                src={localSettings.og_image}
                                alt="Preview"
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Image className="w-12 h-12 text-gray-400" />
                              </div>
                            )}
                          </div>
                          <div className="p-3">
                            <p className="text-gray-500 text-xs uppercase">seusite.vercel.app</p>
                            <p className="text-gray-900 font-semibold text-sm mt-1 line-clamp-2">
                              {localSettings.meta_title || localSettings.name || t('pageBuilder.settings.seo.defaultTitle', 'Titulo da Pagina')}
                            </p>
                            <p className="text-gray-600 text-xs mt-1 line-clamp-2">
                              {localSettings.meta_description || t('pageBuilder.settings.social.defaultDescription', 'Descricao da pagina')}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Analytics Tab - Flux Integration */}
                  {activeTab === 'pixels' && (
                    <div className="space-y-5">
                      <div>
                        <p className="text-sm text-gray-400 mb-4">
                          {t('pageBuilder.settings.pixels.description', 'Vincule os pixels das plataformas de anúncios conectadas para rastrear conversões nesta landing page.')}
                        </p>
                      </div>

                      {isLoadingPixels ? (
                        <div className="flex items-center justify-center py-12">
                          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                        </div>
                      ) : (
                        <div className="space-y-5">
                          {/* Meta Pixel */}
                          {platformPixels.meta.length > 0 && (
                            <div className="p-4 bg-gray-800 rounded-xl border border-gray-700">
                              <div className="flex items-center gap-2 mb-3">
                                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none"><path d="M24 12c0-6.627-5.373-12-12-12S0 5.373 0 12c0 5.99 4.388 10.954 10.125 11.854V15.47H7.078V12h3.047V9.356c0-3.007 1.792-4.668 4.533-4.668 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874V12h3.328l-.532 3.47h-2.796v8.385C19.612 22.954 24 17.99 24 12z" fill="#1877F2"/></svg>
                                <span className="font-medium text-white">Meta Pixel</span>
                              </div>
                              <div className="space-y-2">
                                {platformPixels.meta.map((pixel: any) => {
                                  const linked = isPixelLinked('meta', pixel.id);
                                  return (
                                    <button
                                      key={pixel.id}
                                      onClick={() => togglePixel('meta', pixel.id, pixel.name)}
                                      className={`w-full flex items-center justify-between p-3 rounded-lg border text-left transition-all ${
                                        linked
                                          ? 'border-blue-500 bg-blue-500/10'
                                          : 'border-gray-600 hover:border-gray-500 hover:bg-gray-700/50'
                                      }`}
                                    >
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-white truncate">{pixel.name}</p>
                                        <p className="text-xs text-gray-400">ID: {pixel.id}</p>
                                        {pixel.lastFired && (
                                          <p className="text-xs text-green-400 mt-0.5">
                                            Ativo - {new Date(pixel.lastFired).toLocaleDateString()}
                                          </p>
                                        )}
                                      </div>
                                      {linked ? (
                                        <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0" />
                                      ) : (
                                        <div className="w-5 h-5 rounded-full border-2 border-gray-500 flex-shrink-0" />
                                      )}
                                    </button>
                                  );
                                })}
                              </div>

                              {/* Meta Pixel Configuration - Events & CAPI */}
                              {getLinkedMetaPixel() && (
                                <div className="mt-4 pt-4 border-t border-gray-600 space-y-4">
                                  {/* Events Configuration */}
                                  <div>
                                    <p className="text-sm font-medium text-gray-300 mb-2">Eventos do Pixel</p>
                                    <p className="text-xs text-gray-500 mb-3">Selecione quais eventos o pixel deve rastrear nesta pagina.</p>
                                    <div className="grid grid-cols-2 gap-2">
                                      {META_EVENTS.map(evt => {
                                        const metaPixel = getLinkedMetaPixel()!;
                                        const isActive = (metaPixel.events || ['PageView']).includes(evt);
                                        const isPageView = evt === 'PageView';
                                        return (
                                          <label
                                            key={evt}
                                            className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all text-sm ${
                                              isActive
                                                ? 'border-blue-500/50 bg-blue-500/10 text-white'
                                                : 'border-gray-600 text-gray-400 hover:border-gray-500'
                                            } ${isPageView ? 'opacity-70 cursor-not-allowed' : ''}`}
                                          >
                                            <input
                                              type="checkbox"
                                              checked={isActive}
                                              onChange={() => toggleMetaEvent(evt)}
                                              disabled={isPageView}
                                              className="rounded border-gray-500 bg-gray-700 text-blue-500 focus:ring-blue-500 focus:ring-offset-0"
                                            />
                                            {evt}
                                          </label>
                                        );
                                      })}
                                    </div>
                                  </div>

                                  {/* Conversions API Token */}
                                  <div>
                                    <p className="text-sm font-medium text-gray-300 mb-1">Conversions API Token</p>
                                    <p className="text-xs text-gray-500 mb-2">Token de acesso para enviar eventos server-side via Meta Conversions API (CAPI). Gere em Events Manager &gt; Settings.</p>
                                    <div className="relative">
                                      <input
                                        type={showCapiToken ? 'text' : 'password'}
                                        value={getLinkedMetaPixel()?.conversions_api_token || ''}
                                        onChange={(e) => updateMetaPixelField('conversions_api_token', e.target.value)}
                                        placeholder="EAAxxxxxxx..."
                                        className="w-full px-4 py-2.5 pr-10 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 font-mono"
                                      />
                                      <button
                                        type="button"
                                        onClick={() => setShowCapiToken(!showCapiToken)}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-white transition-colors"
                                      >
                                        {showCapiToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                      </button>
                                    </div>
                                  </div>

                                  {/* Test Event Code */}
                                  <div>
                                    <p className="text-sm font-medium text-gray-300 mb-1">Test Event Code (opcional)</p>
                                    <p className="text-xs text-gray-500 mb-2">Codigo para testar eventos CAPI no Events Manager. Remova antes de ir para producao.</p>
                                    <input
                                      type="text"
                                      value={getLinkedMetaPixel()?.test_event_code || ''}
                                      onChange={(e) => updateMetaPixelField('test_event_code', e.target.value)}
                                      placeholder="TEST12345"
                                      className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 font-mono"
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Google Ads Conversion Actions (multi-select) */}
                          {platformPixels.google.length > 0 && (
                            <div className="p-4 bg-gray-800 rounded-xl border border-gray-700">
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none"><path d="M12.545 10.239v3.821h5.445c-.712 2.315-2.647 3.972-5.445 3.972a6.033 6.033 0 110-12.064c1.498 0 2.866.549 3.921 1.453l2.814-2.814A9.969 9.969 0 0012.545 2C7.021 2 2.543 6.477 2.543 12s4.478 10 10.002 10c8.396 0 10.249-7.85 9.426-11.748l-9.426-.013z" fill="#4285F4"/></svg>
                                  <span className="font-medium text-white">Google Ads</span>
                                </div>
                                {(localSettings.linked_pixels || []).filter(p => p.platform === 'google').length > 0 && (
                                  <span className="text-xs text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded-full">
                                    {(localSettings.linked_pixels || []).filter(p => p.platform === 'google').length} selecionada(s)
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 mb-3">Selecione as acoes de conversao que devem disparar ao enviar formularios nesta pagina.</p>
                              <div className="space-y-2">
                                {platformPixels.google.map((conv: any) => {
                                  const linked = isPixelLinked('google', conv.id);
                                  const linkedPixel = (localSettings.linked_pixels || []).find(p => p.platform === 'google' && p.pixel_id === conv.id);
                                  return (
                                    <div key={conv.id}>
                                      <button
                                        onClick={() => togglePixel('google', conv.id, conv.name, { conversion_label: conv.conversion_label, google_tag_id: conv.google_tag_id, account_id: conv.account_id, account_name: conv.account_name })}
                                        className={`w-full flex items-center justify-between p-3 rounded-lg border text-left transition-all ${
                                          linked
                                            ? 'border-blue-500 bg-blue-500/10'
                                            : 'border-gray-600 hover:border-gray-500 hover:bg-gray-700/50'
                                        }`}
                                      >
                                        <div className="flex-1 min-w-0">
                                          <p className="text-sm font-medium text-white truncate">{conv.name}</p>
                                          <p className="text-xs text-gray-400">
                                            {conv.category || conv.type}
                                            {conv.account_name ? ` · conta: ${conv.account_name}` : ''}
                                            {conv.account_id ? ` (${conv.account_id})` : ''}
                                          </p>
                                        </div>
                                        {linked ? (
                                          <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0" />
                                        ) : (
                                          <div className="w-5 h-5 rounded-full border-2 border-gray-500 flex-shrink-0" />
                                        )}
                                      </button>
                                      {linked && (
                                        <div className="ml-2 mt-2 mb-1">
                                          <div className="flex items-center gap-2">
                                            <input
                                              type="text"
                                              value={linkedPixel?.conversion_label || ''}
                                              onChange={(e) => updateGooglePixelLabel(conv.id, e.target.value)}
                                              placeholder="Conversion Label (ex: AbC-D_efG-h12)"
                                              className="flex-1 px-3 py-1.5 bg-gray-700 border border-gray-600 rounded-lg text-white text-xs placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 font-mono"
                                            />
                                          </div>
                                          {!linkedPixel?.conversion_label && (
                                            <p className="text-xs text-yellow-400 mt-1">Sem conversion label, a conversao nao sera registrada no Google Ads. Cole o label do snippet de evento da sua acao de conversao.</p>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* LinkedIn */}
                          {platformPixels.linkedin.length > 0 && (
                            <div className="p-4 bg-gray-800 rounded-xl border border-gray-700">
                              <div className="flex items-center gap-2 mb-3">
                                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" fill="#0A66C2"/></svg>
                                <span className="font-medium text-white">LinkedIn</span>
                              </div>
                              <div className="space-y-2">
                                {platformPixels.linkedin.map((conv: any) => {
                                  const linked = isPixelLinked('linkedin', conv.id);
                                  return (
                                    <button
                                      key={conv.id}
                                      onClick={() => togglePixel('linkedin', conv.id, conv.name)}
                                      className={`w-full flex items-center justify-between p-3 rounded-lg border text-left transition-all ${
                                        linked
                                          ? 'border-blue-500 bg-blue-500/10'
                                          : 'border-gray-600 hover:border-gray-500 hover:bg-gray-700/50'
                                      }`}
                                    >
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-white truncate">{conv.name}</p>
                                        <p className="text-xs text-gray-400">{conv.type}</p>
                                      </div>
                                      {linked ? (
                                        <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0" />
                                      ) : (
                                        <div className="w-5 h-5 rounded-full border-2 border-gray-500 flex-shrink-0" />
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Google Tag Manager */}
                          <div className="p-4 bg-gray-800 rounded-xl border border-gray-700">
                            <div className="flex items-center gap-2 mb-3">
                              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="#4285F4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              <span className="font-medium text-white">Google Tag Manager</span>
                            </div>
                            <p className="text-xs text-gray-500 mb-3">Insira o ID do container GTM para injetar automaticamente na landing page.</p>
                            <div className="flex gap-2">
                              <input
                                type="text"
                                value={gtmContainerId}
                                onChange={(e) => setGtmContainerId(e.target.value.toUpperCase())}
                                onBlur={(e) => saveGtmContainer(e.target.value)}
                                placeholder="GTM-XXXXXXX"
                                className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 font-mono"
                              />
                              {(localSettings.linked_pixels || []).some(p => p.platform === 'gtm') && (
                                <button
                                  onClick={removeGtmContainer}
                                  className="px-3 py-2 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm hover:bg-red-500/30 transition-colors"
                                >
                                  Remover
                                </button>
                              )}
                            </div>
                            {gtmContainerId && !/^GTM-[A-Z0-9]+$/.test(gtmContainerId.trim().toUpperCase()) && gtmContainerId.trim() !== '' && (
                              <p className="text-xs text-yellow-400 mt-2">Formato esperado: GTM-XXXXXXX</p>
                            )}
                            {(localSettings.linked_pixels || []).some(p => p.platform === 'gtm') && (
                              <p className="text-xs text-green-400 mt-2 flex items-center gap-1">
                                <Check className="w-3 h-3" /> GTM vinculado. Sera injetado ao publicar.
                              </p>
                            )}
                          </div>

                          {/* Empty state */}
                          {platformPixels.meta.length === 0 && platformPixels.google.length === 0 && platformPixels.linkedin.length === 0 && !(localSettings.linked_pixels || []).some(p => p.platform === 'gtm') && (
                            <div className="text-center py-8">
                              <Target className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                              <p className="text-gray-400 text-sm">
                                {t('pageBuilder.settings.pixels.noPixels', 'Nenhum pixel encontrado. Conecte suas contas de anúncios nas configurações para ver os pixels disponíveis.')}
                              </p>
                            </div>
                          )}

                          {/* Info about linked pixels */}
                          {(localSettings.linked_pixels || []).length > 0 && (
                            pixelsAguardandoPublicacao ? (
                              <div className="p-3 bg-amber-500/10 border border-amber-500/40 rounded-lg">
                                <p className="text-xs text-amber-300 font-semibold flex items-center gap-1.5">
                                  <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                                  {(localSettings.linked_pixels || []).length} pixel(s) vinculado(s) — mas ainda NÃO estão no ar.
                                </p>
                                <p className="text-xs text-amber-200/80 mt-1.5 leading-relaxed">
                                  {publishInfo?.lastDeployedAt
                                    ? `A versão publicada é de ${new Date(publishInfo.lastDeployedAt).toLocaleString('pt-BR')} e não contém estes pixels. Clique em Publicar para colocá-los no ar.`
                                    : 'Esta página nunca foi publicada. Clique em Publicar para colocar os pixels no ar.'}
                                </p>
                              </div>
                            ) : (
                              <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                                <p className="text-xs text-green-400 flex items-center gap-1.5">
                                  <Check className="w-3.5 h-3.5" />
                                  {(localSettings.linked_pixels || []).length} pixel(s) vinculado(s) e no ar na versão publicada.
                                </p>
                              </div>
                            )
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'analytics' && (
                    <div className="space-y-6">
                      {/* Flux Hero */}
                      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-violet-600 via-purple-600 to-indigo-600 p-6">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
                        <div className="relative">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 bg-white/20 rounded-xl">
                              <Zap className="w-6 h-6 text-white" />
                            </div>
                            <div>
                              <h3 className="text-xl font-bold text-white">Flux by DevSkin</h3>
                              <p className="text-white/70 text-sm">Analytics & Session Recording</p>
                            </div>
                          </div>
                          <p className="text-white/90 text-sm mb-4">
                            {t('pageBuilder.settings.analytics.fluxDescription', 'Entenda como seus visitantes interagem com sua landing page. Heatmaps, gravacao de sessoes, performance e muito mais.')}
                          </p>
                          <a
                            href="https://flux.devskin.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-white/90 hover:text-white text-sm font-medium"
                          >
                            {t('pageBuilder.settings.analytics.learnMore', 'Saiba mais sobre o Flux')}
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </div>
                      </div>

                      {/* Enable Toggle */}
                      <div className="p-4 bg-gray-800 rounded-xl border border-gray-700">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${localSettings.flux_enabled ? 'bg-green-500/20' : 'bg-gray-700'}`}>
                              <Activity className={`w-5 h-5 ${localSettings.flux_enabled ? 'text-green-400' : 'text-gray-400'}`} />
                            </div>
                            <div>
                              <p className="text-white font-medium">{t('pageBuilder.settings.analytics.enableFlux', 'Ativar Flux Analytics')}</p>
                              <p className="text-gray-400 text-sm">{t('pageBuilder.settings.analytics.enableFluxDesc', 'Coleta automatica de dados ao publicar')}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleChange('flux_enabled', !localSettings.flux_enabled)}
                            className={`relative w-14 h-7 rounded-full transition-colors ${
                              localSettings.flux_enabled ? 'bg-green-500' : 'bg-gray-600'
                            }`}
                          >
                            <div className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
                              localSettings.flux_enabled ? 'translate-x-7' : 'translate-x-0'
                            }`} />
                          </button>
                        </div>
                      </div>

                      {/* Configuration Fields */}
                      {localSettings.flux_enabled && (
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                              {t('pageBuilder.settings.analytics.apiKey', 'API Key do Flux')}
                            </label>
                            <input
                              type="text"
                              value={localSettings.flux_api_key || ''}
                              onChange={(e) => handleChange('flux_api_key', e.target.value)}
                              placeholder="dsk_pub_..."
                              className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-xl text-white placeholder-gray-500 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 font-mono text-sm"
                            />
                            <p className="mt-1 text-xs text-gray-500">
                              {t('pageBuilder.settings.analytics.apiKeyHelp', 'Encontre sua API Key em flux.devskin.com/settings')}
                            </p>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                              {t('pageBuilder.settings.analytics.appId', 'App ID')}
                            </label>
                            <input
                              type="text"
                              value={localSettings.flux_app_id || ''}
                              onChange={(e) => handleChange('flux_app_id', e.target.value)}
                              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                              className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-xl text-white placeholder-gray-500 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 font-mono text-sm"
                            />
                          </div>
                        </div>
                      )}

                      {/* Features Grid */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-4 bg-gray-800/50 rounded-xl border border-gray-700">
                          <Eye className="w-5 h-5 text-purple-400 mb-2" />
                          <p className="text-white font-medium text-sm">{t('pageBuilder.settings.analytics.sessionRecording', 'Gravacao de Sessoes')}</p>
                          <p className="text-gray-400 text-xs mt-1">{t('pageBuilder.settings.analytics.sessionRecordingDesc', 'Assista visitantes navegando')}</p>
                        </div>
                        <div className="p-4 bg-gray-800/50 rounded-xl border border-gray-700">
                          <MousePointer className="w-5 h-5 text-blue-400 mb-2" />
                          <p className="text-white font-medium text-sm">{t('pageBuilder.settings.analytics.heatmaps', 'Heatmaps')}</p>
                          <p className="text-gray-400 text-xs mt-1">{t('pageBuilder.settings.analytics.heatmapsDesc', 'Onde usuarios clicam')}</p>
                        </div>
                        <div className="p-4 bg-gray-800/50 rounded-xl border border-gray-700">
                          <Activity className="w-5 h-5 text-green-400 mb-2" />
                          <p className="text-white font-medium text-sm">{t('pageBuilder.settings.analytics.performance', 'Web Vitals')}</p>
                          <p className="text-gray-400 text-xs mt-1">{t('pageBuilder.settings.analytics.performanceDesc', 'LCP, FID, CLS em tempo real')}</p>
                        </div>
                        <div className="p-4 bg-gray-800/50 rounded-xl border border-gray-700">
                          <BarChart3 className="w-5 h-5 text-orange-400 mb-2" />
                          <p className="text-white font-medium text-sm">{t('pageBuilder.settings.analytics.errorTracking', 'Error Tracking')}</p>
                          <p className="text-gray-400 text-xs mt-1">{t('pageBuilder.settings.analytics.errorTrackingDesc', 'Detecte erros JS automaticamente')}</p>
                        </div>
                      </div>

                      {/* Script Preview */}
                      {localSettings.flux_enabled && localSettings.flux_api_key && localSettings.flux_app_id && (
                        <div className="p-4 bg-gray-800 rounded-xl border border-gray-700">
                          <div className="flex items-center justify-between mb-3">
                            <p className="text-sm font-medium text-gray-300">{t('pageBuilder.settings.analytics.scriptPreview', 'Script que sera injetado:')}</p>
                            <button
                              onClick={copyFluxScript}
                              className="flex items-center gap-2 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-xs text-gray-300 transition-colors"
                            >
                              {copiedScript ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                              {copiedScript ? t('common.copied', 'Copiado!') : t('common.copy', 'Copiar')}
                            </button>
                          </div>
                          <div className="bg-gray-900 rounded-lg p-3 overflow-x-auto">
                            <pre className="text-xs text-gray-400 whitespace-pre-wrap font-mono">
                              {generateFluxScript().slice(0, 300)}...
                            </pre>
                          </div>
                          <p className="mt-2 text-xs text-green-400 flex items-center gap-1">
                            <Check className="w-3 h-3" />
                            {t('pageBuilder.settings.analytics.autoInjected', 'Sera injetado automaticamente ao publicar')}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-700 bg-gray-800/50">
                  <button
                    onClick={onClose}
                    className="px-5 py-2.5 text-gray-300 hover:text-white hover:bg-gray-700 rounded-xl transition-colors"
                  >
                    {t('common.cancel', 'Cancelar')}
                  </button>
                  <button
                    onClick={handleSave}
                    className="px-6 py-2.5 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white font-medium rounded-xl transition-all"
                  >
                    {t('pageBuilder.settings.save', 'Salvar Configuracoes')}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default PageSettingsModal;
